import { LegacyVisibilityModel, ColumnVisibilityModelsType } from './columns/shared/column-types';
import { validateView } from './columns/shared/column-utils';
import { Views } from './columns/views';
import {
  buildVisibilityModel,
  getByViewTab,
  getByMappingName,
} from './columns/legacy/columnVisibilityAdapter';

function splitIdents(idents: string, searchType: string): string[] {
  return searchType === 'CHEMICALS' ? idents.split(/(?<=\d)-(?=C)/) : idents.split('-');
}

function applyDynamicColumnLogic(
  viewName: string,
  model: LegacyVisibilityModel,
  searchItems?: any[],
  urlIdents?: string,
  urlView?: string,
): LegacyVisibilityModel {
  let view: string | null = null;
  let identCount = 0;
  let hasAllIdents = false;

  if (urlIdents && urlView) {
    view = urlView;
    hasAllIdents = urlIdents.toUpperCase() === 'ALL';
    if (!hasAllIdents) {
      const identArray = splitIdents(urlIdents, urlView);
      identCount = identArray.length;
    }
  } else if (searchItems && searchItems.length > 0) {
    identCount = searchItems.length;
    if (viewName.includes('DISEASES')) {
      view = 'DISEASES';
    } else if (viewName.includes('GENES')) {
      view = 'GENES';
    } else if (viewName.includes('VARIANTS')) {
      view = 'VARIANTS';
    } else if (urlView) {
      view = urlView;
    }
  } else {
    const urlParams = new URLSearchParams(window.location.search);
    const idents = urlParams.get('idents');
    view = urlParams.get('view');

    if (idents && view) {
      hasAllIdents = idents.toUpperCase() === 'ALL';
      if (!hasAllIdents) {
        const identArray = splitIdents(idents, view);
        identCount = identArray.length;
      }
    }
  }

  const normalizedView = view?.toUpperCase() ?? null;
  const isSingleIdent = !hasAllIdents && identCount === 1;
  const shouldShowEntityColumnByDefault = hasAllIdents || identCount > 1;

  const applyColumnVisibility = (
    columns: string[],
    visible: boolean,
    baseModel: LegacyVisibilityModel,
  ): LegacyVisibilityModel => {
    const nextModel = { ...baseModel };
    let hasChanges = false;
    columns.forEach(col => {
      if (nextModel[col] !== undefined && nextModel[col] !== visible) {
        nextModel[col] = visible;
        hasChanges = true;
      }
    });
    return hasChanges ? nextModel : baseModel;
  };

  if (normalizedView === 'DISEASES') {
    if (isSingleIdent) return applyColumnVisibility(['diseaseName'], false, model);
    if (shouldShowEntityColumnByDefault) return applyColumnVisibility(['diseaseName'], true, model);
    return model;
  }

  if (normalizedView === 'GENES') {
    if (isSingleIdent) return applyColumnVisibility(['geneSymbol'], false, model);
    if (shouldShowEntityColumnByDefault) return applyColumnVisibility(['geneSymbol'], true, model);
    return model;
  }

  if (normalizedView === 'VARIANTS') {
    const variantColumns = ['variantStrID', 'strID'];
    if (isSingleIdent) return applyColumnVisibility(variantColumns, false, model);
    if (shouldShowEntityColumnByDefault) return applyColumnVisibility(variantColumns, true, model);
    return model;
  }

  if (normalizedView === 'CHEMICALS') {
    const chemicalColumns = ['chemPrefName', 'chemID', 'chemIDs', 'chemsInSentence'];
    if (isSingleIdent) return applyColumnVisibility(chemicalColumns, false, model);
    if (shouldShowEntityColumnByDefault) return applyColumnVisibility(chemicalColumns, true, model);
    return model;
  }

  return model;
}

const ColumnVisibilityModels: ColumnVisibilityModelsType = {} as ColumnVisibilityModelsType;

Object.entries(Views).forEach(([viewName, viewModel]) => {
  validateView(viewModel);
  const baseModel = buildVisibilityModel(viewModel);
  ColumnVisibilityModels[viewName] = baseModel;
});

function hideGeneDiseaseColumns(model: LegacyVisibilityModel): LegacyVisibilityModel {
  const cloned: LegacyVisibilityModel = { ...model };
  Object.keys(cloned).forEach(key => {
    if (key.startsWith('gene') || key.startsWith('disease')) {
      cloned[key] = false;
    }
  });
  return cloned;
}

ColumnVisibilityModels['numberPmidsWithChemsIncludedInEvidenceGDA_EVIDENCE'] = hideGeneDiseaseColumns(
  ColumnVisibilityModels['GDA_ALL_EVIDENCE'] as LegacyVisibilityModel,
);
ColumnVisibilityModels['numberPmidsWithChemsIncludedInEvidenceVDA_EVIDENCE'] = hideGeneDiseaseColumns(
  ColumnVisibilityModels['VDA_ALL_EVIDENCE'] as LegacyVisibilityModel,
);
ColumnVisibilityModels['numPMIDsupportingAssociationGDA_EVIDENCE'] = hideGeneDiseaseColumns(
  ColumnVisibilityModels['GDA_ALL_EVIDENCE'] as LegacyVisibilityModel,
);
ColumnVisibilityModels['numPMIDsupportingAssociationVDA_EVIDENCE'] = hideGeneDiseaseColumns(
  ColumnVisibilityModels['VDA_ALL_EVIDENCE'] as LegacyVisibilityModel,
);
ColumnVisibilityModels['numberChemsIncludedInEvidenceGDA_EVIDENCE'] = hideGeneDiseaseColumns(
  ColumnVisibilityModels['GDA_ALL_EVIDENCE'] as LegacyVisibilityModel,
);
ColumnVisibilityModels['numberChemsIncludedInEvidenceVDA_EVIDENCE'] = hideGeneDiseaseColumns(
  ColumnVisibilityModels['VDA_ALL_EVIDENCE'] as LegacyVisibilityModel,
);

ColumnVisibilityModels['numberPmidsWithChemsIncludedInEvidenceGDA_SUMMARY'] = hideGeneDiseaseColumns(
  ColumnVisibilityModels['GDA_ALL_SUMMARY'] as LegacyVisibilityModel,
);
ColumnVisibilityModels['numberPmidsWithChemsIncludedInEvidenceVDA_SUMMARY'] = hideGeneDiseaseColumns(
  ColumnVisibilityModels['VDA_ALL_SUMMARY'] as LegacyVisibilityModel,
);
ColumnVisibilityModels['numPMIDsupportingAssociationGDA_SUMMARY'] = hideGeneDiseaseColumns(
  ColumnVisibilityModels['GDA_ALL_SUMMARY'] as LegacyVisibilityModel,
);
ColumnVisibilityModels['numPMIDsupportingAssociationVDA_SUMMARY'] = hideGeneDiseaseColumns(
  ColumnVisibilityModels['VDA_ALL_SUMMARY'] as LegacyVisibilityModel,
);
ColumnVisibilityModels['numberNCTSWithChemsIncludedInEvidenceGDA_SUMMARY'] = hideGeneDiseaseColumns(
  ColumnVisibilityModels['GDA_ALL_SUMMARY'] as LegacyVisibilityModel,
);

// Evidence model aliases for entity-based mapping drill-downs.
ColumnVisibilityModels['numDiseasesAssociatedToGene_EVIDENCE'] = ColumnVisibilityModels['GENES_GDA_EVIDENCE'];
ColumnVisibilityModels['numVariantsAssociatedToGene_EVIDENCE'] = ColumnVisibilityModels['GENES_VDA_EVIDENCE'];
ColumnVisibilityModels['numGenesAssociatedToDisease_EVIDENCE'] = ColumnVisibilityModels['GENES_GDA_EVIDENCE'];
ColumnVisibilityModels['numVariantsAssociatedToDisease_EVIDENCE'] =
  ColumnVisibilityModels['GENES_VDA_EVIDENCE'];
ColumnVisibilityModels['numDiseasesAssociatedToVariant_EVIDENCE'] =
  ColumnVisibilityModels['GENES_VDA_EVIDENCE'];

Object.defineProperty(ColumnVisibilityModels, 'getByViewTab', {
  value: function (
    VIEW: string,
    TAB: string,
    isEvidence: boolean,
    searchItems?: any[],
    urlIdents?: string,
    urlView?: string,
  ): LegacyVisibilityModel | undefined {
    const baseModel = getByViewTab(this, VIEW, TAB, isEvidence);
    if (!baseModel) return undefined;

    const viewType = isEvidence ? 'EVIDENCE' : 'SUMMARY';
    const expectedKey = `${VIEW}_${TAB}_${viewType}`;
    return applyDynamicColumnLogic(expectedKey, baseModel, searchItems, urlIdents, urlView);
  },
  enumerable: false,
});

Object.defineProperty(ColumnVisibilityModels, 'getByMappingName', {
  value: function (mappingName: string, isEvidence: boolean): LegacyVisibilityModel | undefined {
    return getByMappingName(this, mappingName, isEvidence);
  },
  enumerable: false,
});

export { ColumnVisibilityModels, Views };
export { buildVisibilityModel } from './columns/legacy/columnVisibilityAdapter';
export { validateView } from './columns/shared/column-utils';
export type {
  ColumnViewModel,
  LegacyVisibilityModel,
  ColumnVisibilityModelsType,
} from './columns/shared/column-types';
